import React, { useEffect, useState, useCallback, useRef } from "react";
import EditStudentModal from "./EditStudentModal";
import AddStudentModal from "./AddStudentModal";
import CreateUserModal from "./CreateUserModal";
import UpdatePasswordModal from "./UpdatePasswordModal";
import jsPDF from "jspdf";
import "jspdf-autotable";
import "../styles/StudentListTabs.css";
import API_URL from "../apiConfig";

// Utility to deduplicate students
function uniqueBy(arr, keyFn) {
  const seen = new Set();
  return arr.filter(item => {
    const key = keyFn(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// Utility to sort students by roll_no (numeric, ascending, blanks last)
function sortByRollNo(arr) {
  return [...arr].sort((a, b) => {
    const aRoll = parseInt(a.roll_no);
    const bRoll = parseInt(b.roll_no);
    if (isNaN(aRoll)) return 1;
    if (isNaN(bRoll)) return -1;
    return aRoll - bRoll;
  });
}

export default function StudentListTabs({ token }) {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [editStudent, setEditStudent] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState("");
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [createUserStudent, setCreateUserStudent] = useState(null);
  const [showUpdatePassword, setShowUpdatePassword] = useState(false);
  const [updatePasswordStudent, setUpdatePasswordStudent] = useState(null);
  const [currentBatch, setCurrentBatch] = useState("");

  const printRef = useRef();
  const printAllRef = useRef();

  // Fetch students
  const fetchStudents = useCallback(() => {
    setLoading(true);
    setError("");
    fetch(`${API_URL}/api/students`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        setStudents(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch((err) => {
        setStudents([]);
        setLoading(false);
        setError("Failed to load students.");
      });
  }, [token]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  // All unique batches (session_year or batch_id fallback), sorted oldest to newest
  const batchSet = new Set();
  students.forEach(s => batchSet.add(s.session_year || s.batch_id || "Unknown"));
  const batches = Array.from(batchSet)
    .filter(b => b)
    .sort((a, b) => {
      const aStart = parseInt(String(a).split('-')[0], 10);
      const bStart = parseInt(String(b).split('-')[0], 10);
      return aStart - bStart;
    });

  useEffect(() => {
    if (!currentBatch && batches.length > 0) setCurrentBatch(batches[0]);
  }, [batches, currentBatch]);

  const studentsForBatch = uniqueBy(
    students.filter(s => (s.session_year || s.batch_id) === currentBatch),
    s => s.id || s.student_id || s.email
  );

  const filteredStudents = sortByRollNo(
    studentsForBatch.filter((s) => {
      const term = searchTerm.trim().toLowerCase();
      if (!term) return true;
      return (
        (s.roll_no && String(s.roll_no).toLowerCase().includes(term)) ||
        (s.batch_id && String(s.batch_id).toLowerCase().includes(term)) ||
        (s.name && s.name.toLowerCase().includes(term)) ||
        (s.address && s.address.toLowerCase().includes(term)) ||
        (s.category && s.category.toLowerCase().includes(term))
      );
    })
  );

  // Actions
  const startEdit = (student) => {
    setEditStudent(student);
    setEditForm({ ...student });
    setShowEdit(true);
  };
  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Classic pattern: expects event!
  const handleEditStudentUpdate = (e) => {
    e.preventDefault();
    fetch(`${API_URL}/api/students/${editForm.id}`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(editForm),
    })
      .then((res) => res.json())
      .then((resData) => {
        if (resData.error) {
          alert(resData.error);
        } else {
          setShowEdit(false);
          setEditStudent(null);
          fetchStudents();
        }
      });
  };

  const handleEditStudentSaveClose = () => {
    setShowEdit(false);
    setEditStudent(null);
    fetchStudents();
  };
  const handleEditStudentCancel = () => {
    setShowEdit(false);
    setEditStudent(null);
  };
  const handleDeleteStudent = (id) => {
    if (!window.confirm("Delete this student?")) return;
    fetch(`${API_URL}/api/students/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((resData) => {
        if (resData.error) {
          alert(resData.error);
        } else {
          fetchStudents();
        }
      });
  };
  const handleAddStudentClick = () => setShowAdd(true);
  const handleAddStudentSave = () => {
    setShowAdd(false);
    fetchStudents();
  };
  const handleAddStudentCancel = () => setShowAdd(false);

  const openCreateUserModal = (student) => {
    setCreateUserStudent(student);
    setShowCreateUser(true);
  };
  const closeCreateUserModal = () => {
    setShowCreateUser(false);
    setCreateUserStudent(null);
  };
  const handleUserCreated = () => {
    closeCreateUserModal();
    fetchStudents();
  };

  const openUpdatePasswordModal = (student) => {
    setUpdatePasswordStudent(student);
    setShowUpdatePassword(true);
  };
  const closeUpdatePasswordModal = () => {
    setShowUpdatePassword(false);
    setUpdatePasswordStudent(null);
  };
  const handlePasswordUpdated = () => {
    closeUpdatePasswordModal();
    fetchStudents();
  };

  // Print table (visible tab)
  const handlePrintTable = (ref) => {
    if (!ref.current) return;
    const win = window.open("", "_blank");
    win.document.write("<html><head><title>Print</title>");
    win.document.write(
      `<style>
      table { border-collapse: collapse; width: 100%; font-size: 15px;}
      th, td { border: 1px solid #888; padding: 7px 10px; }
      th { background: #eaf3ff; color: #184e77;}
      body { font-family: Arial, sans-serif; padding: 22px; }
      h2 { text-align:center; }
      </style>`
    );
    win.document.write("</head><body>");
    win.document.write(ref.current.outerHTML);
    win.document.write("</body></html>");
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 300);
  };

  // PDF download for current tab
  const handleDownloadPDF = (ref, tabBatch) => {
    if (!ref.current) return;
    const doc = new jsPDF();
    doc.text(`Student List - Batch: ${tabBatch}`, 14, 18);
    doc.autoTable({ html: ref.current, startY: 25 });
    doc.save(`StudentList_Batch${tabBatch}.pdf`);
  };

  // PDF download for ALL tabs
  const handleDownloadPDFAll = (ref) => {
    if (!ref.current) return;
    const doc = new jsPDF();
    doc.text(`Student List - All Batches`, 14, 18);
    doc.autoTable({ html: ref.current, startY: 25 });
    doc.save(`StudentList_AllBatches.pdf`);
  };

  function getStudentsForBatch(batch) {
    return sortByRollNo(
      uniqueBy(
        students.filter(s => (s.session_year || s.batch_id) === batch),
        s => s.id || s.student_id || s.email
      )
    );
  }

  // Printable table for students, no actions, sorted by roll_no
  const StudentPrintableTable = React.forwardRef(function StudentPrintableTable({ students, batch, refProp }, ref) {
    const sortedStudents = sortByRollNo(students);
    const tableRef = ref || refProp;
    return (
      <div ref={tableRef}>
        <h3>Batch: {batch}</h3>
        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 18 }}>
          <thead>
            <tr>
              <th>Roll No</th>
              <th>Batch</th>
              <th>Name</th>
              <th>Category</th>
              <th>Address</th>
            </tr>
          </thead>
          <tbody>
            {sortedStudents.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ textAlign: "center" }}>No students found.</td>
              </tr>
            ) : (
              sortedStudents.map(student => (
                <tr key={student.id || student.student_id || student.email}>
                  <td style={{ textAlign: "center" }}>{student.roll_no || ""}</td>
                  <td style={{ textAlign: "center" }}>{student.session_year || student.batch_id || ""}</td>
                  <td>{student.name || student.student_name || ""}</td>
                  <td>{student.category || "N/A"}</td>
                  <td>{student.address || ""}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    );
  });

  return (
    <div className="student-list-tabs-container">
      <h2 className="student-list-title">Student List</h2>
      <div className="student-list-controls-row">
        <div className="student-batch-tabs-wrapper">
          <div className="student-batch-tabs">
            {batches.map(b => (
              <button
                key={b}
                className={`student-batch-tab${currentBatch === b ? " active" : ""}`}
                onClick={() => setCurrentBatch(b)}
              >
                {b}
              </button>
            ))}
          </div>
        </div>
        <input
          type="text"
          className="student-search-input"
          placeholder="Search by roll no, batch, name, address, category"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          autoFocus
        />
        <div className="student-list-actions">
          <button className="student-btn student-btn-add" onClick={handleAddStudentClick}>
            + Add Student
          </button>
          <button className="student-btn" onClick={() => handlePrintTable(printRef)}>
            Print This Batch
          </button>
          <button className="student-btn" onClick={() => handleDownloadPDF(printRef, currentBatch)}>
            Download PDF
          </button>
          <button className="student-btn dark" onClick={() => handlePrintTable(printAllRef)}>
            Print All Batches
          </button>
          <button className="student-btn dark" onClick={() => handleDownloadPDFAll(printAllRef)}>
            Download All PDF
          </button>
        </div>
      </div>
      <div className="student-list-table-wrapper">
        {/* Hidden printable tables */}
        <div style={{display:"none"}}>
          <StudentPrintableTable refProp={printRef} students={filteredStudents} batch={currentBatch} />
          <div ref={printAllRef}>
            <h2>Student List: All Batches</h2>
            {batches.map(b => (
              <div key={b} style={{marginBottom: 22}}>
                <h3>Batch: {b}</h3>
                <StudentPrintableTable students={getStudentsForBatch(b)} batch={b} />
              </div>
            ))}
          </div>
        </div>

        {/* Visible table */}
        <table className="student-list-table">
          <thead>
            <tr>
              <th>Roll No</th>
              <th>Batch</th>
              <th>Name</th>
              <th>Category</th>
              <th>Address</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} style={{ textAlign: "center" }}>
                  Loading students...
                </td>
              </tr>
            ) : filteredStudents.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: "center" }}>
                  No students found.
                </td>
              </tr>
            ) : (
              filteredStudents.map((student) => (
                <tr key={student.id || student.student_id || student.email}>
                  <td style={{ textAlign: "center" }}>{student.roll_no || ""}</td>
                  <td style={{ textAlign: "center" }}>{student.session_year || student.batch_id || ""}</td>
                  <td>{student.name || student.student_name || ""}</td>
                  <td>{student.category || "N/A"}</td>
                  <td>{student.address || ""}</td>
                  <td>
                    <div className="student-btn-group">
                      <button onClick={() => startEdit(student)} className="student-btn edit">Edit</button>
                      <button onClick={() => handleDeleteStudent(student.id)} className="student-btn delete">Delete</button>
                      {!student.user_id && (
                        <button className="student-btn create-user" onClick={() => openCreateUserModal(student)}>
                          Create User
                        </button>
                      )}
                      {student.user_id && (
                        <button className="student-btn update-password" onClick={() => openUpdatePasswordModal(student)}>
                          Update Password
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {error && <div style={{ color: "red", marginTop: 10 }}>{error}</div>}
      {showEdit && editStudent && (
        <EditStudentModal
          show={showEdit}
          form={editForm}
          handleChange={handleEditFormChange}
          handleEditStudentUpdate={handleEditStudentUpdate} // classic pattern!
          handleEditStudentSaveClose={handleEditStudentSaveClose}
          handleEditStudentCancel={handleEditStudentCancel}
          mode="edit"
        />
      )}
      {showAdd && (
        <AddStudentModal
          show={showAdd}
          token={token}
          onClose={handleAddStudentCancel}
          onSave={handleAddStudentSave}
        />
      )}
      {showCreateUser && createUserStudent && (
        <CreateUserModal
          show={showCreateUser}
          onClose={closeCreateUserModal}
          student={createUserStudent}
          token={token}
          onUserCreated={handleUserCreated}
        />
      )}
      {showUpdatePassword && updatePasswordStudent && (
        <UpdatePasswordModal
          show={showUpdatePassword}
          onClose={closeUpdatePasswordModal}
          userType="student"
          user={updatePasswordStudent}
          token={token}
          onUpdated={handlePasswordUpdated}
        />
      )}
    </div>
  );
}